import { InjectionToken } from '@angular/core';
import { ResourceType } from 'json-api';
import { map, Observable, ReplaySubject, share } from 'rxjs';
import { AtomService } from '../core/atom.service';
import { AtomResource } from '../core/classes/atom-resource';
import { Translatable } from '../translation/translatable';
import { EnumsService } from '../shared/services/enums.service';
import { Enum } from '../core/resources/enum.resource';

export type FilterPropertyType = 'string' | 'number' | 'boolean';

export interface FilterData<F extends Filter = Filter> {
  filter: F;
  operation: F['operations'][number]['value'];
  value: string | number;
}

export interface FilterOperation {
  readonly value: string;
  readonly label: string;
}

export abstract class Filter {
  abstract readonly type: string;
  abstract readonly operations: Readonly<FilterOperation[]>;
  abstract readonly defaultOperation: string;

  public label: string;

  public defaultFilter: boolean;
  public removable: boolean;

  constructor(
    public readonly path: string,
    label?: string,
    defaultFilter: boolean = false,
    removable: boolean = true
  ) {
    this.label = label ?? path.replace(/[\._-]/, ' ');
    this.defaultFilter = defaultFilter;
    this.removable = removable;
  }
}
export class CurrentFilter extends Filter {
  readonly type = 'current';
  readonly operations = [{ value: 'equal', label: 'Equals' }] as const;
  readonly defaultOperation = 'equal' as const;
}

export class StringFilter extends Filter {
  readonly type = 'string';
  readonly operations = [
    { value: 'like', label: 'Contains' },
    { value: 'equal', label: 'Equals' },
    { value: 'exclude', label: 'Not contains' },
    { value: 'isset', label: 'Is setted' },
  ] as const;
  readonly defaultOperation = 'like' as const;
}

export class BooleanFilter extends Filter {
  readonly type = 'boolean';
  readonly operations = [
    { value: 'equal', label: 'Equals' },
    { value: 'exclude', label: 'Not contains' },
  ] as const;
  readonly defaultOperation = 'equal' as const;
}

export class ListFilter extends Filter {
  readonly type = 'list';
  readonly operations = [
    { value: 'equal', label: 'Equals' },
    { value: 'exclude', label: 'Not equals' },
  ] as const;
  readonly defaultOperation = 'equal' as const;
  readonly resources!: { value: string; label: string }[];

  constructor(
    values: { value: string; label: string }[],
    path: string,
    label?: string,
    defaultFilter?: boolean,
    removable?: boolean
  ) {
    super(path, label, defaultFilter, removable);
    this.resources = values;
  }
}

export class NumberFilter extends Filter {
  readonly type = 'number';
  readonly operations = [
    { value: 'equal', label: 'Equals' },
    { value: 'exclude', label: 'Not equals' },
    { value: 'greater', label: 'Greater than' },
    { value: 'egreater', label: 'Greater or equals' },
    { value: 'lower', label: 'Lower than' },
    { value: 'elower', label: 'Lower or equals' },
    { value: 'isset', label: 'Is setted' },
  ] as const;
  readonly defaultOperation = 'equal' as const;
}

export class EnumListFilter<
  R extends AtomResource & { name?: Translatable | string }
> extends Filter {
  readonly type = 'enumList';
  readonly operations = [
    { value: 'equal', label: 'Equals' },
    { value: 'exclude', label: 'Not equals' },
  ] as const;
  readonly defaultOperation = 'equal' as const;
  readonly resources$: Observable<Enum[]>;
  enums!: EnumsService;

  constructor(
    atoms: AtomService,
    table: string,
    field: string,
    path: string,
    label?: string,
    defaultFilter?: boolean,
    removable?: boolean
  ) {
    super(path, label, defaultFilter, removable);
    this.enums = new EnumsService(atoms);
    this.resources$ = this.enums.byTableAndField(table, field).pipe(
      share({
        connector: () => new ReplaySubject(0),
        resetOnComplete: true,
        resetOnError: true,
        resetOnRefCountZero: true,
      })
    );
  }
}

export class ResourceListFilter<
  R extends AtomResource & { name?: Translatable | string }
> extends Filter {
  readonly type = 'resourceList';
  readonly operations = [
    { value: 'equal', label: 'Equals' },
    { value: 'exclude', label: 'Not equals' },
    { value: 'isset', label: 'Is setted' },
  ] as const;
  readonly defaultOperation = 'equal' as const;
  readonly resources$: Observable<R[]>;

  constructor(
    atom: AtomService,
    resourceType: ResourceType<R>,
    path: string,
    label?: string,
    defaultFilter?: boolean,
    removable?: boolean
  ) {
    super(path, label, defaultFilter, removable);
    this.resources$ = atom.all(resourceType).pipe(
      map((ret) => ret?.data),
      share({
        connector: () => new ReplaySubject(0),
        resetOnComplete: true,
        resetOnError: true,
        resetOnRefCountZero: true,
      })
    );
  }
}

export const FILTERS = new InjectionToken<Filter[]>('Avaiable filters', {
  providedIn: 'root',
  factory: () => [
    new NumberFilter('id', 'ID'),
    new StringFilter('name', 'Name'),
  ],
});

export class ResourcesFilter extends Filter {
  readonly type = 'list';
  readonly operations = [
    { value: 'equal', label: 'Equals' },
    { value: 'exclude', label: 'Not equals' },
  ] as const;
  readonly defaultOperation = 'equal' as const;
  resources!: { value: string; label: string }[];

  constructor(
    atom: AtomService,
    onlyEnum:boolean = false,
    path: string,
    data:string ='table',
    label?: string,
    defaultFilter?: boolean,
    removable?: boolean
  ) {
    super(path, label, defaultFilter, removable);
    if(data === 'table'){
      atom.getRessourcesOrAttributes(onlyEnum).subscribe((res: any)=>{
        this.resources = res.sort().map((value:string)=>({value,label:value}))
      })
    }
    else if(data === 'field'){
      this.resources = []
      atom.getRessourcesOrAttributes(onlyEnum,true).subscribe((res:any)=>{
        this.resources = [...new Set(res.flat())].sort().map((value:any)=>({value,label:value}))
      })
    }
  }
}

export class DateFilter extends Filter {
  readonly type = 'date';
  readonly operations = [
    { value: 'equal', label: 'Equals' },
    { value: 'exclude', label: 'Not equals' },
    { value: 'greater', label: 'Greater than' },
    { value: 'lower', label: 'Lower than' },
  ] as const;
  readonly defaultOperation = 'equal' as const;
}
export class ValidityFilter extends Filter {
  readonly type = 'date';
  readonly operations = [
    { value: 'equal', label: 'Equals' },
    { value: 'greater', label: 'Greater than' },
    { value: 'lower', label: 'Lower than' },
  ] as const;
  readonly defaultOperation = 'equal' as const;
}

